import { useState, useCallback, useEffect } from 'react';

type TransitionDirection = 'forward' | 'backward';

interface UsePageTransitionReturn {
  direction: number;
  setDirection: (dir: TransitionDirection) => void;
  triggerTransition: (newPage: string, dir?: TransitionDirection) => void;
  currentPage: string;
}

// Map URL paths to page names
const pathToPageMap: Record<string, string> = {
  '/': 'home',
  '/login': 'login',
  '/signup': 'signup',
  '/signup/consent': 'signup-consent',
  '/dashboard': 'dashboard',
  '/journey-setup': 'journey-setup',
  '/admin': 'admin',
  '/dashboard-selector': 'dashboard-selector',
  '/analytics': 'analytics',
  // Legacy /single-optimize URL routes to the Agent page (single-optimize is deprecated)
  '/single-optimize': 'agent',
  '/smart-optimize': 'dashboard',
  '/history': 'history',
  '/my-activities': 'my-activities',
  '/my-documents': 'dashboard',
  '/resume-builder': 'resume-builder',
  '/support': 'support',
  '/product-feedback': 'product-feedback',
  '/profile': 'profile',
  '/credits': 'credits',
  '/prepare-for-interview': 'prepare-for-interview',
  '/collaborate': 'collaborate',
  '/jobs': 'jobs',
  '/admin/jobs': 'jobs-admin',
  '/ai-companion': 'ai-companion',
  '/agent': 'agent',
  '/calendar-notes': 'calendar-notes',
  '/objectives-tasks': 'objectives-tasks',
  '/forgot-password': 'forgot-password',
  '/reset-password': 'reset-password',
  '/auth/confirm': 'auth-confirm',
  '/privacy-policy': 'privacy-policy',
  '/cookie-policy': 'cookie-policy',
  '/terms-of-service': 'terms-of-service',
  '/gdpr-compliance': 'gdpr-compliance',
  '/dpdp-compliance': 'dpdp-compliance',
  '/refund-cancellation': 'refund-cancellation',
  '/faqs': 'faqs',
  '/about-us': 'about-us',
  '/blog': 'blog',
  '/admin/blogs': 'blog-cms',
  '/admin/blogs/new': 'blog-editor',
  '/dashboard/careers': 'careers-cms',
  '/dashboard/careers/new': 'careers-editor',
  '/contact-us': 'contact-us',
  '/payment-success': 'payment-success',
  '/payment-history': 'payment-history',
  '/profile-info': 'profile-info',
  '/debug-logs': 'debug-logs',
  '/phone-registration': 'phone-registration',
  '/developer': 'developer',
  '/account-settings': 'account-settings',
  '/skill-assessment': 'skill-assessment',
  '/admin/control-panel': 'control-panel',
  '/admin/cron-jobs': 'cron-jobs-admin',
  '/admin/testimonials': 'testimonials-admin',
  '/admin/translations': 'translations-admin',
};

// Map page names to URL paths
const pageToPathMap: Record<string, string> = {
  'home': '/',
  'login': '/login',
  'signup': '/signup',
  'signup-consent': '/signup/consent',
  'dashboard': '/dashboard',
  'journey-setup': '/journey-setup',
  'analytics': '/analytics',
  'admin': '/admin',
  'dashboard-selector': '/dashboard-selector',
  // Deprecated: redirect to /agent if the legacy page name reaches the URL builder.
  'single-optimize': '/agent',
  'history': '/history',
  'my-activities': '/my-activities',
  'resume-builder': '/resume-builder',
  'support': '/support',
  'product-feedback': '/product-feedback',
  'profile': '/profile',
  'credits': '/credits',
  'prepare-for-interview': '/prepare-for-interview',
  'collaborate': '/collaborate',
  'jobs': '/jobs',
  'jobs-admin': '/admin/jobs',
  'ai-companion': '/ai-companion',
  'agent': '/agent',
  'calendar-notes': '/calendar-notes',
  'objectives-tasks': '/objectives-tasks',
  'forgot-password': '/forgot-password',
  'reset-password': '/reset-password',
  'auth-confirm': '/auth/confirm',
  'privacy-policy': '/privacy-policy',
  'cookie-policy': '/cookie-policy',
  'terms-of-service': '/terms-of-service',
  'gdpr-compliance': '/gdpr-compliance',
  'dpdp-compliance': '/dpdp-compliance',
  'refund-cancellation': '/refund-cancellation',
  'faqs': '/faqs',
  'about-us': '/about-us',
  'blog': '/blog',
  'blog-cms': '/admin/blogs',
  'blog-editor': '/admin/blogs/new',
  'careers-cms': '/dashboard/careers',
  'careers-editor': '/dashboard/careers/new',
  'contact-us': '/contact-us',
  'payment-success': '/payment-success',
  'payment-history': '/payment-history',
  'profile-info': '/profile-info',
  'debug-logs': '/debug-logs',
  'phone-registration': '/phone-registration',
  'developer': '/developer',
  'account-settings': '/account-settings',
  'skill-assessment': '/skill-assessment',
  'control-panel': '/admin/control-panel',
  'cron-jobs-admin': '/admin/cron-jobs',
  'testimonials-admin': '/admin/testimonials',
  'translations-admin': '/admin/translations',
};

// Get current page from URL
const getCurrentPageFromURL = (): string => {
  const pathname = window.location.pathname;
  if (/^\/admin\/blogs\/[^/]+\/edit$/.test(pathname)) return 'blog-editor';
  if (/^\/dashboard\/careers\/[^/]+\/edit$/.test(pathname)) return 'careers-editor';
  return pathToPageMap[pathname] || 'home';
};

export const usePageTransition = (initialPage: string = ''): UsePageTransitionReturn => {
  const [direction, setDirectionState] = useState(1);
  const [currentPage, setCurrentPage] = useState(() => {
    // Initialize from URL if available, otherwise use initialPage
    if (typeof window !== 'undefined') {
      const pageFromURL = getCurrentPageFromURL();
      return pageFromURL;
    }
    return initialPage || 'home';
  });

  const setDirection = useCallback((dir: TransitionDirection) => {
    setDirectionState(dir === 'forward' ? 1 : -1);
  }, []);

  const triggerTransition = useCallback((newPage: string, dir: TransitionDirection = 'forward') => {
    setDirection(dir);
    setCurrentPage(newPage);
    
    // Update browser URL
    const newPath = pageToPathMap[newPage] || '/dashboard';
    if (window.location.pathname !== newPath) {
      window.history.pushState({ page: newPage }, '', newPath);
    }
  }, [setDirection]);

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const newPage = getCurrentPageFromURL();
      if (newPage !== currentPage) {
        setCurrentPage(newPage);
        // Don't change direction for browser navigation to avoid jarring transitions
        setDirectionState(1);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentPage]);

  // Update page state if URL changes programmatically
  useEffect(() => {
    const pageFromURL = getCurrentPageFromURL();
    if (pageFromURL !== currentPage && window.location.pathname !== '/') {
      setCurrentPage(pageFromURL);
    }
  }, [currentPage]);

  return {
    direction,
    setDirection,
    triggerTransition,
    currentPage,
  };
};
